import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, UserCheck, UserX, Clock, Trash2, CheckCircle, XCircle, Pencil, Plus, Shield, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

export default function UserManagement() {
    const { api, isMaster, isAdmin } = useAuth();
    const [users, setUsers] = useState([]);
    const [pendingUsers, setPendingUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [createMasterDialogOpen, setCreateMasterDialogOpen] = useState(false);
    const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    
    const [editFormData, setEditFormData] = useState({
        name: '',
        email: '',
        password: ''
    });

    const [masterFormData, setMasterFormData] = useState({
        name: '',
        email: '',
        password: ''
    });

    const [userFormData, setUserFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'user',
        company_id: ''
    });

    const fetchData = async () => {
        try {
            const requests = [
                api.get('/users'),
                api.get('/users/pending')
            ];
            
            // Master needs companies list
            if (isMaster) {
                requests.push(api.get('/companies'));
            }
            
            const responses = await Promise.all(requests);
            setUsers(responses[0].data);
            setPendingUsers(responses[1].data);
            if (isMaster && responses[2]) {
                setCompanies(responses[2].data);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Erro ao carregar utilizadores');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleApprove = async (userId) => {
        try {
            await api.put(`/users/${userId}/status`, { is_active: true });
            toast.success('Utilizador aprovado com sucesso');
            fetchData();
        } catch (error) {
            console.error('Error approving user:', error);
            toast.error(error.response?.data?.detail || 'Erro ao aprovar utilizador');
        }
    };

    const handleReject = async (userId) => {
        try {
            await api.delete(`/users/${userId}`);
            toast.success('Utilizador rejeitado e eliminado');
            fetchData();
        } catch (error) {
            console.error('Error rejecting user:', error);
            toast.error(error.response?.data?.detail || 'Erro ao rejeitar utilizador');
        }
    };

    const handleToggleStatus = async (user) => {
        try {
            await api.put(`/users/${user.id}/status`, { is_active: !user.is_active });
            toast.success(`Utilizador ${!user.is_active ? 'ativado' : 'desativado'}`);
            fetchData();
        } catch (error) {
            console.error('Error toggling user:', error);
            toast.error(error.response?.data?.detail || 'Erro ao alterar estado');
        }
    };

    const handleDelete = async (userId) => {
        try {
            await api.delete(`/users/${userId}`);
            toast.success('Utilizador eliminado com sucesso');
            fetchData();
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error(error.response?.data?.detail || 'Erro ao eliminar utilizador');
        }
    };

    const handleOpenEditDialog = (user) => {
        setEditingUser(user);
        setEditFormData({
            name: user.name,
            email: user.email,
            password: ''
        });
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingUser(null);
        setEditFormData({ name: '', email: '', password: '' });
    };

    const handleEditUser = async (e) => {
        e.preventDefault();
        try {
            const data = {
                name: editFormData.name,
                email: editFormData.email
            };
            if (editFormData.password) {
                data.password = editFormData.password;
            }
            
            await api.put(`/users/${editingUser.id}`, data);
            toast.success('Utilizador atualizado com sucesso');
            handleCloseEditDialog();
            fetchData();
        } catch (error) {
            console.error('Error updating user:', error);
            toast.error(error.response?.data?.detail || 'Erro ao atualizar utilizador');
        }
    };

    const handleCloseCreateMasterDialog = () => {
        setCreateMasterDialogOpen(false);
        setMasterFormData({ name: '', email: '', password: '' });
    };

    const handleCreateMaster = async (e) => {
        e.preventDefault();
        try {
            await api.post('/users/master', masterFormData);
            toast.success('Novo utilizador Master criado com sucesso');
            handleCloseCreateMasterDialog();
            fetchData();
        } catch (error) {
            console.error('Error creating master:', error);
            toast.error(error.response?.data?.detail || 'Erro ao criar utilizador Master');
        }
    };

    const handleCloseCreateUserDialog = () => {
        setCreateUserDialogOpen(false);
        setUserFormData({ name: '', email: '', password: '', role: 'user', company_id: '' });
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            const data = {
                name: userFormData.name,
                email: userFormData.email,
                password: userFormData.password,
                role: userFormData.role
            };
            
            // Master must specify company
            if (isMaster) {
                data.company_id = userFormData.company_id;
            }
            
            await api.post('/users', data);
            toast.success('Utilizador criado com sucesso');
            handleCloseCreateUserDialog();
            fetchData();
        } catch (error) {
            console.error('Error creating user:', error);
            toast.error(error.response?.data?.detail || 'Erro ao criar utilizador');
        }
    };

    const formatDate = (dateString) => {
        return format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: pt });
    };

    const getRoleBadge = (role) => {
        switch (role) {
            case 'master':
                return <Badge className="bg-amber-100 text-amber-700">Master</Badge>;
            case 'admin':
                return <Badge className="bg-blue-100 text-blue-700">Admin</Badge>;
            default:
                return <Badge variant="secondary">Utilizador</Badge>;
        }
    };

    if (loading) {
        return (
            <div className="space-y-6" data-testid="users-loading">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-96 rounded-lg" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="users-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold font-heading text-slate-900">Gestão de Utilizadores</h1>
                    <p className="text-slate-500">Aprove e gerencie os utilizadores da sua empresa</p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Create User Button */}
                    <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-user-btn">
                                <UserPlus className="w-4 h-4 mr-2" />
                                Criar Utilizador
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                                <DialogTitle className="font-heading">Criar Novo Utilizador</DialogTitle>
                                <DialogDescription>
                                    {isMaster 
                                        ? 'Crie um novo utilizador e associe-o a uma empresa'
                                        : 'Crie um novo utilizador na sua empresa'
                                    }
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleCreateUser} className="space-y-4">
                                {isMaster && (
                                    <div className="space-y-2">
                                        <Label htmlFor="user_company">Empresa</Label>
                                        <Select
                                            value={userFormData.company_id}
                                            onValueChange={(value) => setUserFormData({ ...userFormData, company_id: value })}
                                        >
                                            <SelectTrigger data-testid="user-company-select">
                                                <SelectValue placeholder="Selecione a empresa" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {companies.map((company) => (
                                                    <SelectItem key={company.id} value={company.id}>
                                                        {company.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <Label htmlFor="user_name">Nome</Label>
                                    <Input
                                        id="user_name"
                                        value={userFormData.name}
                                        onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                                        required
                                        data-testid="user-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="user_email">Email</Label>
                                    <Input
                                        id="user_email"
                                        type="email"
                                        value={userFormData.email}
                                        onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                                        required
                                        data-testid="user-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="user_password">Password</Label>
                                    <Input
                                        id="user_password"
                                        type="password"
                                        value={userFormData.password}
                                        onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                                        required
                                        data-testid="user-password-input"
                                    />
                                </div>
                                {isMaster && (
                                    <div className="space-y-2">
                                        <Label htmlFor="user_role">Função</Label>
                                        <Select
                                            value={userFormData.role}
                                            onValueChange={(value) => setUserFormData({ ...userFormData, role: value })}
                                        >
                                            <SelectTrigger data-testid="user-role-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="user">Utilizador</SelectItem>
                                                <SelectItem value="admin">Administrador</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-slate-500">
                                            Administradores podem gerir utilizadores da empresa
                                        </p>
                                    </div>
                                )}
                                <div className="p-3 rounded-lg bg-emerald-50 text-emerald-700 text-sm">
                                    <p>O utilizador será criado já ativo e poderá fazer login imediatamente.</p>
                                </div>
                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={handleCloseCreateUserDialog}>
                                        Cancelar
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        className="bg-emerald-600 hover:bg-emerald-700" 
                                        disabled={isMaster && !userFormData.company_id}
                                        data-testid="user-save-btn"
                                    >
                                        Criar Utilizador
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>

                    {/* Create Master Button (only for master) */}
                    {isMaster && (
                        <Dialog open={createMasterDialogOpen} onOpenChange={setCreateMasterDialogOpen}>
                            <DialogTrigger asChild>
                                <Button className="bg-amber-600 hover:bg-amber-700" data-testid="create-master-btn">
                                    <Shield className="w-4 h-4 mr-2" />
                                    Criar Master
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                                <DialogHeader>
                                    <DialogTitle className="font-heading">Criar Novo Master</DialogTitle>
                                    <DialogDescription>
                                        Crie um novo utilizador com acesso Master à plataforma
                                    </DialogDescription>
                                </DialogHeader>
                                <form onSubmit={handleCreateMaster} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="master_name">Nome</Label>
                                        <Input
                                            id="master_name"
                                            value={masterFormData.name}
                                            onChange={(e) => setMasterFormData({ ...masterFormData, name: e.target.value })}
                                            required
                                            data-testid="master-name-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="master_email">Email</Label>
                                        <Input
                                            id="master_email"
                                            type="email"
                                            value={masterFormData.email}
                                            onChange={(e) => setMasterFormData({ ...masterFormData, email: e.target.value })}
                                            required
                                            data-testid="master-email-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="master_password">Password</Label>
                                        <Input
                                            id="master_password"
                                            type="password"
                                            value={masterFormData.password}
                                            onChange={(e) => setMasterFormData({ ...masterFormData, password: e.target.value })}
                                            required
                                            data-testid="master-password-input"
                                        />
                                    </div>
                                    <div className="p-3 rounded-lg bg-amber-50 text-amber-700 text-sm">
                                        <p className="font-medium">Atenção</p>
                                        <p>Utilizadores Master têm acesso total a todas as empresas e funcionalidades da plataforma.</p>
                                    </div>
                                    <DialogFooter>
                                        <Button type="button" variant="outline" onClick={handleCloseCreateMasterDialog}>
                                            Cancelar
                                        </Button>
                                        <Button type="submit" className="bg-amber-600 hover:bg-amber-700" data-testid="master-save-btn">
                                            Criar Master
                                        </Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </div>

            {/* Edit User Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Editar Utilizador</DialogTitle>
                        <DialogDescription>
                            Atualize os dados do utilizador
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEditUser} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit_name">Nome</Label>
                            <Input
                                id="edit_name"
                                value={editFormData.name}
                                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                required
                                data-testid="edit-user-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit_email">Email</Label>
                            <Input
                                id="edit_email"
                                type="email"
                                value={editFormData.email}
                                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                                required
                                data-testid="edit-user-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit_password">Nova Password (deixe vazio para manter)</Label>
                            <Input
                                id="edit_password"
                                type="password"
                                value={editFormData.password}
                                onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                                placeholder="••••••••"
                                data-testid="edit-user-password"
                            />
                        </div>
                        {editingUser && (
                            <div className="p-3 rounded-lg bg-slate-50 text-sm">
                                <p><strong>Função:</strong> {editingUser.role === 'master' ? 'Master' : editingUser.role === 'admin' ? 'Admin' : 'Utilizador'}</p>
                                {editingUser.company_name && <p><strong>Empresa:</strong> {editingUser.company_name}</p>}
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={handleCloseEditDialog}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="edit-user-save-btn">
                                Guardar
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Tabs defaultValue={pendingUsers.length > 0 ? "pending" : "all"}>
                <TabsList>
                    <TabsTrigger value="pending" className="relative">
                        <Clock className="w-4 h-4 mr-2" />
                        Pendentes
                        {pendingUsers.length > 0 && (
                            <Badge className="ml-2 bg-amber-500 text-white">{pendingUsers.length}</Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="all">
                        <Users className="w-4 h-4 mr-2" />
                        Todos ({users.length})
                    </TabsTrigger>
                </TabsList>

                {/* Pending Users */}
                <TabsContent value="pending">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg font-heading">Utilizadores Pendentes de Aprovação</CardTitle>
                            <CardDescription>Novos registos aguardando a sua aprovação</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {pendingUsers.length === 0 ? (
                                <div className="text-center py-12" data-testid="no-pending">
                                    <UserCheck className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                                    <h3 className="font-semibold text-slate-900 mb-2">Sem pedidos pendentes</h3>
                                    <p className="text-slate-500">Todos os utilizadores estão aprovados</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {pendingUsers.map((user) => (
                                        <div
                                            key={user.id}
                                            className="flex items-center justify-between p-4 rounded-lg border bg-amber-50/50 border-amber-200"
                                            data-testid={`pending-user-${user.id}`}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center">
                                                    <span className="text-sm font-semibold text-amber-700">
                                                        {user.name?.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className="font-medium text-slate-900">{user.name}</p>
                                                    <p className="text-sm text-slate-500">{user.email}</p>
                                                    <p className="text-xs text-slate-400">Registado em {formatDate(user.created_at)}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    size="sm"
                                                    className="bg-emerald-600 hover:bg-emerald-700"
                                                    onClick={() => handleApprove(user.id)}
                                                    data-testid={`approve-user-${user.id}`}
                                                >
                                                    <CheckCircle className="w-4 h-4 mr-1" />
                                                    Aprovar
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                                    onClick={() => handleReject(user.id)}
                                                    data-testid={`reject-user-${user.id}`}
                                                >
                                                    <XCircle className="w-4 h-4 mr-1" />
                                                    Rejeitar
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* All Users */}
                <TabsContent value="all">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg font-heading">Todos os Utilizadores</CardTitle>
                            <CardDescription>Lista completa de utilizadores</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {users.length === 0 ? (
                                <div className="text-center py-12" data-testid="no-users">
                                    <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                                    <h3 className="font-semibold text-slate-900 mb-2">Sem utilizadores</h3>
                                    <p className="text-slate-500">Nenhum utilizador registado ainda</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow className="data-table-header">
                                            <TableHead>Utilizador</TableHead>
                                            <TableHead>Email</TableHead>
                                            {isMaster && <TableHead>Empresa</TableHead>}
                                            <TableHead>Função</TableHead>
                                            <TableHead>Estado</TableHead>
                                            <TableHead>Registado</TableHead>
                                            <TableHead className="text-right">Ações</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {users.map((user) => (
                                            <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                                                            <span className="text-xs font-semibold text-slate-600">
                                                                {user.name?.charAt(0).toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <span className="font-medium">{user.name}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>{user.email}</TableCell>
                                                {isMaster && <TableCell>{user.company_name || '-'}</TableCell>}
                                                <TableCell>{getRoleBadge(user.role)}</TableCell>
                                                <TableCell>
                                                    <Badge variant={user.is_active ? 'default' : 'secondary'} className={user.is_active ? 'bg-emerald-100 text-emerald-700' : ''}>
                                                        {user.is_active ? 'Ativo' : 'Inativo'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-sm text-slate-500">
                                                    {formatDate(user.created_at)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleOpenEditDialog(user)}
                                                            data-testid={`edit-user-${user.id}`}
                                                        >
                                                            <Pencil className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleToggleStatus(user)}
                                                            disabled={user.role === 'admin' && !isMaster}
                                                            data-testid={`toggle-user-${user.id}`}
                                                        >
                                                            {user.is_active ? (
                                                                <UserX className="w-4 h-4" />
                                                            ) : (
                                                                <UserCheck className="w-4 h-4" />
                                                            )}
                                                        </Button>
                                                        <AlertDialog>
                                                            <AlertDialogTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                    disabled={(user.role === 'admin' && !isMaster) || (user.role === 'master' && users.filter(u => u.role === 'master').length <= 1)}
                                                                    data-testid={`delete-user-${user.id}`}
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                            </AlertDialogTrigger>
                                                            <AlertDialogContent>
                                                                <AlertDialogHeader>
                                                                    <AlertDialogTitle>Eliminar Utilizador?</AlertDialogTitle>
                                                                    <AlertDialogDescription>
                                                                        Esta ação não pode ser revertida. O utilizador será permanentemente eliminado.
                                                                    </AlertDialogDescription>
                                                                </AlertDialogHeader>
                                                                <AlertDialogFooter>
                                                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                                    <AlertDialogAction
                                                                        className="bg-red-600 hover:bg-red-700"
                                                                        onClick={() => handleDelete(user.id)}
                                                                    >
                                                                        Eliminar
                                                                    </AlertDialogAction>
                                                                </AlertDialogFooter>
                                                            </AlertDialogContent>
                                                        </AlertDialog>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
